import { JUDGE_POSITIONS } from "score-common";
import { classNumberLabel } from "./classDisplay";

// #2365 (scribe-presence workstream, card D). Trello card #2365, comment
// from Jaybill McCarthy, 2026-09-07: GET /presence (score-api, #2363) only
// ever returns a raw lastSeenAt, never an isOnline verdict -- deciding a
// "quiet too long" threshold is explicitly a DISPLAY decision each consumer
// makes for itself, and each consumer is EXPECTED to pick a different
// number (announcer-fe/#2366 is a call-the-next-rider decision and needs a
// tighter one). Scoreadmin is a whole-show glance, so this one is
// deliberately loose: 2.5 minutes of silence. Starting value, not a
// researched figure -- confirm in practice, per the card.
export const SCRIBE_QUIET_THRESHOLD_MS = 150000;

export function presenceKey({ ring, seat, classId }) {
  return `${ring ?? ""}#${seat ?? ""}#${classId ?? ""}`;
}

// Indexes a GET /presence response by (ring, seat, classId) (#2365, #2370).
// GET /presence now returns every row seen inside its bounded window
// (PRESENCE_MAX_AGE_SECONDS, one hour) tagged `live` true/false, instead of
// hard-dropping a row at 60s. So "was here, went quiet" is answered by the
// response alone, and survives a page reload: this dashboard keeps NO memory
// of earlier polls (the sticky observation map this replaced is gone).
// A seat with no row in the response is "not connected", with no elapsed
// time shown, never a fabricated silence.
//
// Two instances can claim the same key (a tablet swap leaves the old
// instance's stale row beside the new one's live row); the row with the
// freshest lastSeenAt wins, so a stale leftover never masks a live seat.
export function indexPresenceByKey(entries) {
  const byKey = {};
  for (const entry of entries || []) {
    if (!entry || !entry.ring || !entry.seat) continue;
    const key = presenceKey(entry);
    const existing = byKey[key];
    if (existing && (parseTimestampMs(existing.lastSeenAt) ?? -Infinity) > (parseTimestampMs(entry.lastSeenAt) ?? -Infinity)) {
      continue;
    }
    byKey[key] = entry;
  }
  return byKey;
}

// The judged positions a class actually stages, in JUDGE_POSITIONS order --
// the same rule ClassManager.jsx uses to decide which seats are worth a
// scribe picker (FVD's judge panel, #555): only positions with at least one
// real judgeAssignments entry. This is "staffed on the daysheet" (AC2) --
// independent of whether scoreadmin's OWN scribeAssignments tool has anyone
// slotted in, since that's a different (internal) fact from what FVD's own
// panel schedules.
export function positionsForClass(classItem) {
  if (!classItem) return [];
  return JUDGE_POSITIONS.filter(
    (p) => Array.isArray(classItem.judgeAssignments?.[p]) && classItem.judgeAssignments[p].length
  );
}

// #3239: the Station Monitor read model's lastSeenAt can be the newest
// snapshotAt of a station that never heartbeat, and a snapshot's timestamp is
// minted by score-api as `<epoch ms>-<hex>` (mintSnapshotTimestamp), not ISO.
// `new Date("1759876543210-ab12cd34")` is Invalid Date, so the elapsed label
// printed "NaNm NaNs". Parse every shape we know about; anything else is null.
const EPOCH_MS_STAMP = /^(\d{10,13})(?:-[0-9a-f]+)?$/i;

export function parseTimestampMs(value) {
  if (value == null || value === "") return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (typeof value !== "string") return null;
  const stamp = EPOCH_MS_STAMP.exec(value.trim());
  const ms = stamp ? Number(stamp[1]) : new Date(value).getTime();
  return Number.isFinite(ms) ? ms : null;
}

// Milliseconds since `value`, or null when the time is missing or unreadable,
// so callers show their "never"/unknown label instead of NaN.
export function elapsedMsSince(value, now) {
  const ms = parseTimestampMs(value);
  return ms == null ? null : Math.max(0, now - ms);
}

function buildRow({ ring, seat, classId, observed, now, quietThresholdMs, unexpected }) {
  const lastSeenAt = observed?.lastSeenAt ?? null;
  const elapsedMs = elapsedMsSince(lastSeenAt, now);
  return {
    ring,
    seat,
    // Carried through so the render key can be presenceKey(row) (same
    // (ring, seat, classId) identity usePresence's observation map already
    // keys on) rather than a bare `${ring}-${seat}` -- a ring with no
    // current class can surface two "unexpected" observations at the same
    // (ring, seat) under two different classIds (e.g. a stale class-A
    // heartbeat still inside its TTL alongside a fresh class-B one), which
    // collided as duplicate React keys before this field existed.
    classId,
    rideId: observed?.rideId ?? null,
    // #3647: the tablet this row's presence came from. It is the Station
    // Monitor row's deviceId, which is how a station's exception finds its row.
    instanceId: observed?.instanceId ?? null,
    scribeName: observed?.scribeName ?? null,
    lastSeenAt,
    // The server's own fact (#2370): lastSeenAt was within
    // PRESENCE_TTL_SECONDS of the server clock at poll time. The quiet
    // threshold below stays this app's own (150s), reachable now that the
    // API returns rows well past 60s.
    live: observed?.live ?? null,
    elapsedMs,
    everConnected: elapsedMs != null,
    quiet: elapsedMs != null && elapsedMs >= quietThresholdMs,
    unexpected,
  };
}

// Builds the flat (ring, seat) row list the table renders (AC4): one row per
// staffed seat in every ring currently holding a class, in ring/seat order
// (AC1/AC2), PLUS any live presence entry claiming a ring/seat that ring's
// current class panel doesn't stage -- surfaced, never dropped, per #2363's
// "claim, not authorization fact" contract note.
export function buildPresenceRows({
  ringRows,
  observedByKey,
  now,
  quietThresholdMs = SCRIBE_QUIET_THRESHOLD_MS,
}) {
  const rows = [];
  const seenKeys = new Set();

  for (const ringRow of ringRows || []) {
    const { ring, class: currentClass } = ringRow;
    const classId = currentClass?.id ?? null;
    const expectedSeats = positionsForClass(currentClass);

    for (const seat of expectedSeats) {
      const key = presenceKey({ ring, seat, classId });
      seenKeys.add(key);
      const observed = observedByKey[key] || null;
      rows.push(buildRow({ ring, seat, classId, observed, now, quietThresholdMs, unexpected: false }));
    }

    // Any observation reported for THIS ring -- for this ring's CURRENT
    // class when there is one, or at all when the ring has no known current
    // class -- at a seat the expected set above didn't already cover.
    for (const [key, observed] of Object.entries(observedByKey || {})) {
      if (seenKeys.has(key)) continue;
      if (observed.ring !== ring) continue;
      if (currentClass && (observed.classId ?? null) !== classId) continue;
      seenKeys.add(key);
      rows.push(
        buildRow({
          ring,
          seat: observed.seat,
          classId: observed.classId ?? null,
          observed,
          now,
          quietThresholdMs,
          unexpected: true,
        })
      );
    }
  }

  return rows;
}

// "45s" / "3m 12s" / "12m" / "27 hours" / "3 days" -- a plain elapsed-time
// label (#3525). Up to 60 minutes it counts minutes and seconds; over 60
// minutes it reads in whole hours (floored), and over 48 hours in whole days
// (floored), because "1651m 41s" means nothing to a person standing in a ring.
// Only the i18n KEY wrapping this value is a static literal (CLAUDE.md: no
// dynamic i18n keys); this string itself is an interpolated value, not a
// translated key.
const MS_PER_HOUR = 60 * 60 * 1000;
export function formatElapsedLabel(ms) {
  if (ms == null || !Number.isFinite(ms)) return null;
  if (ms > 48 * MS_PER_HOUR) {
    const days = Math.floor(ms / (24 * MS_PER_HOUR));
    return days === 1 ? "1 day" : `${days} days`;
  }
  if (ms > MS_PER_HOUR) {
    const hours = Math.floor(ms / MS_PER_HOUR);
    return hours === 1 ? "1 hour" : `${hours} hours`;
  }
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes === 0) return `${seconds}s`;
  if (seconds === 0) return `${minutes}m`;
  return `${minutes}m ${seconds}s`;
}

// #3376 (TRI-277): the class (number + name) and CNO a station last pinged
// from. The heartbeat carries classId and rideId only while a ride is open on
// the scoring form, so a row with no rideId is blank (never an error, never a
// guess from the ring's current class). CNO is resolved client side from the
// show's ride list that useRingStates already loaded (rideId ->
// competitorNumber). A ride the list does not know yet (e.g. just added)
// still shows its class, with a blank CNO.
export function presenceWork(row, { classesById, ridesById } = {}) {
  if (!row?.rideId) return { classLabel: null, cno: null };
  const cls = row.classId ? classesById?.[row.classId] ?? null : null;
  const classLabel = cls ? classNumberLabel(cls) || null : null;
  const cnoRaw = ridesById?.[row.rideId]?.competitorNumber;
  const cno = cnoRaw != null && `${cnoRaw}`.trim() !== "" ? `${cnoRaw}` : null;
  return { classLabel, cno };
}
